import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  DialogActions,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Tooltip,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
  AttachMoney as AttachMoneyIcon,
  CalendarMonth as CalendarMonthIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  FormatListNumbered as FormatListNumberedIcon,
  GridOn as GridOnIcon,
  GridView as GridViewIcon,
  Payments as PaymentsIcon,
  PriceChange as PriceChangeIcon,
  Print as PrintIcon,
  ReceiptLong as ReceiptLongIcon,
  Scale as ScaleIcon,
  ShoppingCart as ShoppingCartIcon,
  TrendingUp as TrendingUpIcon,
  Paid as PaidIcon,
  TableChart as TableChartIcon,
  ViewInAr as ViewInArIcon,
} from '@mui/icons-material';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths,
} from 'date-fns';
import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import ventasInformesService, {
  VentasResumenProductoRow,
  VentasRpt01Row,
} from '../../services/ventasInformesService';
import SearchableSelect from '../../components/SearchableSelect';
import InformesBackButton from '../../components/shared/InformesBackButton';
import { TIPO_ARTICULO_OPTIONS, labelTipoArticulo } from '../../config/tipoArticuloOptions';
import { ResumenClasificacionTabs } from './InformeVentasResumenProductoClasificacionViews';
import productoService from '../../services/productoService';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  aggregateByField,
  conteosClasificacionUnicos,
  filasAgregadasToExport,
  pctDelMonto,
  pctRentabilidad,
} from '../../utils/agruparVentasResumenProducto';

type ProductoBusquedaItem = {
  codigoArticulo: string;
  descripcion?: string | null;
};

const WEEK_OPTS = { weekStartsOn: 1 as const };

/** NroTipoFormulario en BP_RPT_Ventas01 / maestro de comprobantes de venta. */
const TIPOS_COMPROBANTE_INFORME_VENTAS = [
  { value: '10', label: 'Cotización' },
  { value: '12', label: 'Pedido' },
  { value: '1', label: 'Factura' },
  { value: '2', label: 'Nota de débito' },
  { value: '3', label: 'Nota de crédito' },
] as const;

function isNotaCreditoDetalle(r: VentasRpt01Row): boolean {
  const nroTipo = String(r.nroTipoFormulario ?? '').trim();
  if (nroTipo === '3' || nroTipo === '4') return true;
  const tipU = (r.tipDescripcion ?? '').trim().toUpperCase();
  return tipU.includes('NOTA') && (tipU.includes('CRÉDITO') || tipU.includes('CREDITO'));
}

function signoVentaResumenNC(r: VentasRpt01Row): number {
  return isNotaCreditoDetalle(r) ? -1 : 1;
}

function toIsoDate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

const currencyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 2,
});

const qtyFormatter = new Intl.NumberFormat('es-AR', {
  maximumFractionDigits: 0,
});

const cantidadSumFormatter = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

const decimal3Formatter = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

const pctFormatter = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** Letra–punto de venta (5)–número (8), alineado a facturación y pedidos en el front. */
function formatoComprobanteDetalle(r: VentasRpt01Row): string {
  const letraRaw = (r.letra ?? '').trim();
  const sinDatos =
    !letraRaw && r.nroSuc == null && r.nroComprobante == null;
  if (sinDatos) return '—';
  const L = letraRaw || '-';
  const suc = String(r.nroSuc ?? 0).padStart(5, '0');
  const nroNum = Number(r.nroComprobante ?? 0);
  const nroStr = String(Number.isNaN(nroNum) ? 0 : nroNum).padStart(8, '0');
  return `${L}-${suc}-${nroStr}`;
}

/** Abreviaturas habituales en ventas cuando el maestro usa códigos fijos (complementa tipDescripcion). */
const VENTAS_TIPO_FORM_ABBREV: Record<string, string> = {
  '1': 'Factura',
  '3': 'ND',
  '4': 'NC',
  '9': 'REM',
  '10': 'COT',
  '11': 'AJ',
  '12': 'PED',
};

function tipoComprobanteDetalleLabel(r: VentasRpt01Row): string {
  const tip = (r.tipDescripcion ?? '').trim();
  const tipU = tip.toUpperCase();
  const forU = (r.forDescripcion ?? '').trim().toUpperCase();

  if (tipU.includes('NOTA') && (tipU.includes('CRÉDITO') || tipU.includes('CREDITO'))) return 'NC';
  if (tipU.includes('NOTA') && (tipU.includes('DÉBITO') || tipU.includes('DEBITO'))) return 'ND';
  if (tipU.includes('FACTURA') || forU.includes('FACTURA')) return 'Factura';
  if (tipU.includes('PEDIDO')) return 'PED';
  if (tipU.includes('COTIZ')) return 'COT';
  if (tipU.includes('REMITO')) return 'REM';
  if (tipU.includes('RECIBO') || forU.includes('RECIBO')) return 'REC';
  if (tipU.includes('AJUSTE') || tipU.includes('INVENTARIO')) return 'AJ';

  const nroTipo = (r.nroTipoFormulario ?? '').toString().trim();
  const fromCode = VENTAS_TIPO_FORM_ABBREV[nroTipo];
  if (fromCode) return fromCode;

  if (tip) return tip;
  const ford = (r.forDescripcion ?? '').trim();
  if (ford) return ford;
  return nroTipo || '—';
}

function fechaFmtDetalle(s?: string | null): string {
  if (!s) return '—';
  try {
    return format(new Date(s), 'dd/MM/yyyy');
  } catch {
    return '—';
  }
}

/** Períodos rápidos de fechas (alineado a contabilidad/libro-mayor). */
type QuickChipVentas =
  | 'hoy'
  | 'semana'
  | 'mes'
  | 'mesPasado'
  | 'tresMeses'
  | 'seisMeses'
  | 'anioActual';

const QUICK_CHIP_VENTAS_KEYS: QuickChipVentas[] = [
  'hoy',
  'semana',
  'mes',
  'mesPasado',
  'tresMeses',
  'seisMeses',
  'anioActual',
];

function computeQuickRangeVentas(which: QuickChipVentas): { desde: string; hasta: string } {
  const now = new Date();
  switch (which) {
    case 'hoy': {
      const d = toIsoDate(now);
      return { desde: d, hasta: d };
    }
    case 'semana':
      return {
        desde: toIsoDate(startOfWeek(now, WEEK_OPTS)),
        hasta: toIsoDate(endOfWeek(now, WEEK_OPTS)),
      };
    case 'mes':
      return {
        desde: toIsoDate(startOfMonth(now)),
        hasta: toIsoDate(endOfMonth(now)),
      };
    case 'mesPasado': {
      const ref = subMonths(now, 1);
      return {
        desde: toIsoDate(startOfMonth(ref)),
        hasta: toIsoDate(endOfMonth(ref)),
      };
    }
    case 'tresMeses':
      return {
        desde: toIsoDate(startOfDay(subMonths(now, 3))),
        hasta: toIsoDate(endOfDay(now)),
      };
    case 'seisMeses':
      return {
        desde: toIsoDate(startOfDay(subMonths(now, 6))),
        hasta: toIsoDate(endOfDay(now)),
      };
    case 'anioActual':
      return {
        desde: toIsoDate(startOfYear(now)),
        hasta: toIsoDate(endOfYear(now)),
      };
    default: {
      const _exhaustive: never = which;
      return _exhaustive;
    }
  }
}

function findMatchingQuickChipVentas(desde: string, hasta: string): QuickChipVentas | null {
  for (const key of QUICK_CHIP_VENTAS_KEYS) {
    const r = computeQuickRangeVentas(key);
    if (desde === r.desde && hasta === r.hasta) return key;
  }
  return null;
}

type KpiTarjetaProps = {
  icon: React.ReactNode;
  titulo: string;
  valor: string;
  detalle?: string;
  tooltip?: string;
  colorIcono: string;
};

const KpiTarjeta: React.FC<KpiTarjetaProps> = ({
  icon,
  titulo,
  valor,
  detalle,
  tooltip,
  colorIcono,
}) => {
  const contenido = (
    <Paper
      elevation={0}
      sx={{
        p: 1.75,
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            color: colorIcono,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${colorIcono}14`,
            borderRadius: 1.5,
            p: 0.85,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
            {titulo}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, mt: 0.25 }}>
            {valor}
          </Typography>
          {detalle ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {detalle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
  return tooltip ? (
    <Tooltip title={tooltip} arrow placement="top">
      <Box sx={{ height: '100%' }}>{contenido}</Box>
    </Tooltip>
  ) : (
    contenido
  );
};

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const InformeVentasResumenProducto: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const generadoDesdeUrlRef = useRef(false);

  const [fechaDesde, setFechaDesde] = useState(() => {
    const d = searchParams.get('fechaDesde');
    if (d && ISO_DATE_RE.test(d)) return d;
    return computeQuickRangeVentas('mes').desde;
  });
  const [fechaHasta, setFechaHasta] = useState(() => {
    const h = searchParams.get('fechaHasta');
    if (h && ISO_DATE_RE.test(h)) return h;
    return computeQuickRangeVentas('mes').hasta;
  });
  /** Vacío = todos los tipos de artículo. */
  const [tipoArticulo, setTipoArticulo] = useState('');
  const [nombreRamo, setNombreRamo] = useState('');
  const [nombreLinea, setNombreLinea] = useState('');
  const [nombreClase, setNombreClase] = useState('');
  /** Por defecto factura, ND y NC (NC resta en el resumen). */
  const [tiposComprobanteSel, setTiposComprobanteSel] = useState<string[]>(() => ['1', '2', '3']);
  const [codigoArticuloFiltro, setCodigoArticuloFiltro] = useState('');
  const [descripcionArticuloFiltro, setDescripcionArticuloFiltro] = useState('');
  const [dialogProductoOpen, setDialogProductoOpen] = useState(false);
  const [productoSearchTerm, setProductoSearchTerm] = useState('');
  const [productoSearchLoading, setProductoSearchLoading] = useState(false);
  const [productosEncontrados, setProductosEncontrados] = useState<ProductoBusquedaItem[]>([]);

  const [opcionesRamos, setOpcionesRamos] = useState<string[]>([]);
  const [opcionesLineas, setOpcionesLineas] = useState<string[]>([]);
  const [opcionesClases, setOpcionesClases] = useState<string[]>([]);

  const [rows, setRows] = useState<VentasResumenProductoRow[]>([]);
  const [totales, setTotales] = useState({
    totalCantidad: 0,
    totalMonto: 0,
    totalCosto: 0,
    totalPeso: 0,
    totalVolumen: 0,
  });
  const [cantidadComprobantesDistintos, setCantidadComprobantesDistintos] = useState(0);

  const [loading, setLoading] = useState(false);
  const [loadingOpciones, setLoadingOpciones] = useState(false);

  const [detalleOpen, setDetalleOpen] = useState(false);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [detalleRows, setDetalleRows] = useState<VentasRpt01Row[]>([]);
  const [detalleTitulo, setDetalleTitulo] = useState('');
  const [detalleClave, setDetalleClave] = useState<{
    producto: string;
    tipo: string | null | undefined;
    nombreRamo: string | null | undefined;
    nombreLinea: string | null | undefined;
    nombreClase: string | null | undefined;
  } | null>(null);

  const cargarOpciones = useCallback(async () => {
    if (!fechaDesde || !fechaHasta) return;
    try {
      setLoadingOpciones(true);
      const op = await ventasInformesService.getOpcionesResumenProducto(fechaDesde, fechaHasta);
      setOpcionesRamos(op.nombreRamos ?? []);
      setOpcionesLineas(op.nombreLineas ?? []);
      setOpcionesClases(op.nombreClases ?? []);
    } catch {
      enqueueSnackbar('No se pudieron cargar las opciones de filtro.', { variant: 'warning' });
    } finally {
      setLoadingOpciones(false);
    }
  }, [fechaDesde, fechaHasta, enqueueSnackbar]);

  useEffect(() => {
    void cargarOpciones();
  }, [cargarOpciones]);

  const presetActivo = useMemo(
    () => findMatchingQuickChipVentas(fechaDesde, fechaHasta),
    [fechaDesde, fechaHasta]
  );
  const periodoPersonalizado = Boolean(fechaDesde && fechaHasta && presetActivo === null);

  const resumenFiltrosAvanzados = useMemo(() => {
    const partes: string[] = [];
    if (codigoArticuloFiltro.trim() || descripcionArticuloFiltro.trim()) partes.push('Producto');
    if (tipoArticulo) partes.push('Tipo de artículo');
    if (nombreRamo) partes.push('Ramo');
    if (nombreLinea) partes.push('Línea');
    if (nombreClase) partes.push('Clase');
    if (tiposComprobanteSel.join(',') !== '1,2,3') partes.push('Comprobantes');
    if (periodoPersonalizado) partes.push('Fechas personalizadas');
    return partes.length > 0 ? partes.join(' · ') : '';
  }, [
    codigoArticuloFiltro,
    descripcionArticuloFiltro,
    tipoArticulo,
    nombreRamo,
    nombreLinea,
    nombreClase,
    tiposComprobanteSel,
    periodoPersonalizado,
  ]);

  const indicadoresResumen = useMemo(() => {
    const { totalCantidad: cq, totalMonto: m, totalCosto: c, totalPeso: p, totalVolumen: v } = totales;
    const nComp = cantidadComprobantesDistintos;
    const ticketPromedio = nComp > 0 ? m / nComp : null;
    const precioMedioUnitario = Math.abs(Number(cq)) > 1e-9 ? m / Number(cq) : null;
    const rentabilidadPct = pctRentabilidad(m, c);
    const utilidadBruta = m - c;
    return { ticketPromedio, precioMedioUnitario, rentabilidadPct, utilidadBruta, nComp, cq, m, c, p, v };
  }, [totales, cantidadComprobantesDistintos]);

  const conteosClasificacion = useMemo(() => conteosClasificacionUnicos(rows), [rows]);

  const applyQuickChip = useCallback((which: QuickChipVentas) => {
    const r = computeQuickRangeVentas(which);
    setFechaDesde(r.desde);
    setFechaHasta(r.hasta);
  }, []);

  const toggleTipoComprobante = useCallback((value: string) => {
    setTiposComprobanteSel((prev) => {
      const next = prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value];
      return next.sort((a, b) => Number(a) - Number(b));
    });
  }, []);

  const buscarProductos = useCallback(
    async (searchTermOverride?: string) => {
      const term = (searchTermOverride ?? productoSearchTerm).trim();
      try {
        setProductoSearchLoading(true);
        setProductoSearchTerm(term);
        const data = await productoService.getProductos({
          searchTerm: term || undefined,
          pageNumber: 1,
          pageSize: 30,
        });
        setProductosEncontrados(
          (data.items || []).map((p) => ({
            codigoArticulo: p.codigoArticulo,
            descripcion: p.descripcion,
          }))
        );
      } catch (e) {
        enqueueSnackbar(getApiErrorMessage(e, 'No se pudieron buscar productos'), { variant: 'error' });
      } finally {
        setProductoSearchLoading(false);
      }
    },
    [productoSearchTerm, enqueueSnackbar]
  );

  const seleccionarProducto = useCallback((producto: ProductoBusquedaItem) => {
    setCodigoArticuloFiltro(producto.codigoArticulo ?? '');
    setDescripcionArticuloFiltro(producto.descripcion ?? '');
    setProductoSearchTerm(producto.descripcion?.trim() || producto.codigoArticulo);
    setDialogProductoOpen(false);
  }, []);

  const abrirBuscadorProductos = useCallback(() => {
    const term = descripcionArticuloFiltro.trim() || codigoArticuloFiltro.trim();
    setDialogProductoOpen(true);
    void buscarProductos(term);
  }, [descripcionArticuloFiltro, codigoArticuloFiltro, buscarProductos]);

  const nroTiposFormularioParam = tiposComprobanteSel.join(',');

  const handleGenerar = useCallback(async () => {
    if (!fechaDesde || !fechaHasta) {
      enqueueSnackbar('Indicá fecha desde y hasta.', { variant: 'warning' });
      return;
    }
    if (tiposComprobanteSel.length === 0) {
      enqueueSnackbar('Seleccioná al menos un tipo de comprobante.', { variant: 'warning' });
      return;
    }
    try {
      setLoading(true);
      const res = await ventasInformesService.getResumenProducto({
        fechaDesde,
        fechaHasta,
        tipoArticulo: tipoArticulo || undefined,
        nombreRamo: nombreRamo || undefined,
        nombreLinea: nombreLinea || undefined,
        nombreClase: nombreClase || undefined,
        codigoArticulo: codigoArticuloFiltro.trim() || undefined,
        nroTiposFormulario: nroTiposFormularioParam,
      });
      setRows(res.data);
      setTotales({
        totalCantidad: res.totalCantidad,
        totalMonto: res.totalMonto,
        totalCosto: res.totalCosto ?? 0,
        totalPeso: res.totalPeso,
        totalVolumen: res.totalVolumen,
      });
      setCantidadComprobantesDistintos(Number(res.cantidadComprobantesDistintos ?? 0));
      enqueueSnackbar(`${res.data.length} grupo(s) cargado(s).`, { variant: 'success' });
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudo generar el informe.', { variant: 'error' });
      setRows([]);
      setTotales({
        totalCantidad: 0,
        totalMonto: 0,
        totalCosto: 0,
        totalPeso: 0,
        totalVolumen: 0,
      });
      setCantidadComprobantesDistintos(0);
    } finally {
      setLoading(false);
    }
  }, [
    fechaDesde,
    fechaHasta,
    tipoArticulo,
    nombreRamo,
    nombreLinea,
    nombreClase,
    codigoArticuloFiltro,
    nroTiposFormularioParam,
    enqueueSnackbar,
  ]);

  const filasProductoExport = useMemo(
    () => [...rows].sort((a, b) => b.montoTotal - a.montoTotal),
    [rows]
  );

  useEffect(() => {
    const d = searchParams.get('fechaDesde');
    const h = searchParams.get('fechaHasta');
    if (!d || !h || !ISO_DATE_RE.test(d) || !ISO_DATE_RE.test(h)) return;
    if (generadoDesdeUrlRef.current) return;
    generadoDesdeUrlRef.current = true;
    void handleGenerar();
  }, [searchParams, handleGenerar]);

  const exportarCsv = () => {
    if (rows.length === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'info' });
      return;
    }
    const lines: string[] = [];

    lines.push('--- Resumen por Ramo ---');
    lines.push('Ramo;Cantidad;Monto Total;% Monto;Costo Total;% Rentabilidad;Peso;Volumen');
    for (const f of aggregateByField(rows, 'nombreRamo', totales.totalMonto)) {
      const pctRent = pctRentabilidad(f.montoTotal, f.costoTotal);
      lines.push(
        [
          `"${f.nombre.replace(/"/g, '""')}"`,
          f.cantidad,
          f.montoTotal,
          f.pctMonto,
          f.costoTotal,
          pctRent != null ? Math.round(pctRent * 10) / 10 : '',
          f.pesoTotal,
          f.volumenTotal,
        ].join(';')
      );
    }
    lines.push('');
    lines.push('--- Resumen por Línea ---');
    lines.push('Línea;Cantidad;Monto Total;% Monto;Costo Total;% Rentabilidad;Peso;Volumen');
    for (const f of aggregateByField(rows, 'nombreLinea', totales.totalMonto)) {
      const pctRent = pctRentabilidad(f.montoTotal, f.costoTotal);
      lines.push(
        [
          `"${f.nombre.replace(/"/g, '""')}"`,
          f.cantidad,
          f.montoTotal,
          f.pctMonto,
          f.costoTotal,
          pctRent != null ? Math.round(pctRent * 10) / 10 : '',
          f.pesoTotal,
          f.volumenTotal,
        ].join(';')
      );
    }
    lines.push('');
    lines.push('--- Resumen por Clase ---');
    lines.push('Clase;Cantidad;Monto Total;% Monto;Costo Total;% Rentabilidad;Peso;Volumen');
    for (const f of aggregateByField(rows, 'nombreClase', totales.totalMonto)) {
      const pctRent = pctRentabilidad(f.montoTotal, f.costoTotal);
      lines.push(
        [
          `"${f.nombre.replace(/"/g, '""')}"`,
          f.cantidad,
          f.montoTotal,
          f.pctMonto,
          f.costoTotal,
          pctRent != null ? Math.round(pctRent * 10) / 10 : '',
          f.pesoTotal,
          f.volumenTotal,
        ].join(';')
      );
    }
    lines.push('');
    lines.push('--- Detalle por producto ---');
    lines.push(
      [
        'Producto',
        'Tipo de artículo',
        'Ramo',
        'Línea',
        'Clase',
        'Cantidad',
        'Monto Total',
        '% Monto',
        'Costo Total',
        '% Rentabilidad',
        'Peso Total (kg)',
        'Volumen Total (m³)',
      ].join(';')
    );
    for (const r of filasProductoExport) {
      const pct = Math.round(pctDelMonto(r.montoTotal, totales.totalMonto) * 100) / 100;
      const pctRent = pctRentabilidad(r.montoTotal, r.costoTotal ?? 0);
      lines.push(
        [
          `"${(r.producto ?? '').replace(/"/g, '""')}"`,
          `"${labelTipoArticulo(r.tipo).replace(/"/g, '""')}"`,
          `"${(r.nombreRamo ?? '').replace(/"/g, '""')}"`,
          `"${(r.nombreLinea ?? '').replace(/"/g, '""')}"`,
          `"${(r.nombreClase ?? '').replace(/"/g, '""')}"`,
          String(r.cantidad),
          String(r.montoTotal),
          String(pct),
          String(r.costoTotal ?? 0),
          pctRent != null ? String(Math.round(pctRent * 10) / 10) : '',
          String(r.pesoTotal),
          String(r.volumenTotal),
        ].join(';')
      );
    }
    const totalPctRent = pctRentabilidad(totales.totalMonto, totales.totalCosto);
    lines.push(
      [
        '"TOTAL GENERAL"',
        '',
        '',
        '',
        '',
        String(totales.totalCantidad),
        String(totales.totalMonto),
        '100',
        String(totales.totalCosto),
        totalPctRent != null ? String(Math.round(totalPctRent * 10) / 10) : '',
        String(totales.totalPeso),
        String(totales.totalVolumen),
      ].join(';')
    );

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `informe-ventas-producto_${fechaDesde}_${fechaHasta}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportarExcel = () => {
    if (rows.length === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'info' });
      return;
    }
    const wb = XLSX.utils.book_new();

    const appendResumenHoja = (nombre: string, field: 'nombreRamo' | 'nombreLinea' | 'nombreClase') => {
      const data = filasAgregadasToExport(
        aggregateByField(rows, field, totales.totalMonto),
        field === 'nombreRamo' ? 'ramo' : field === 'nombreLinea' ? 'linea' : 'clase'
      );
      const keys = Object.keys(data[0] ?? { Total: '' });
      const totalRow: Record<string, string | number> = {};
      for (const k of keys) totalRow[k] = '';
      const labelKey = keys[0];
      if (labelKey) totalRow[labelKey] = 'TOTAL GENERAL';
      if (keys.includes('Cantidad')) totalRow.Cantidad = totales.totalCantidad;
      if (keys.includes('Monto Total')) totalRow['Monto Total'] = totales.totalMonto;
      if (keys.includes('% Monto')) totalRow['% Monto'] = 100;
      if (keys.includes('Costo Total')) totalRow['Costo Total'] = totales.totalCosto;
      if (keys.includes('% Rentabilidad')) {
        const pct = pctRentabilidad(totales.totalMonto, totales.totalCosto);
        totalRow['% Rentabilidad'] = pct != null ? Math.round(pct * 10) / 10 : '';
      }
      if (keys.includes('Peso Total (kg)')) totalRow['Peso Total (kg)'] = totales.totalPeso;
      if (keys.includes('Volumen Total (m³)')) totalRow['Volumen Total (m³)'] = totales.totalVolumen;
      data.push(totalRow);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), nombre);
    };
    appendResumenHoja('Ramo', 'nombreRamo');
    appendResumenHoja('Linea', 'nombreLinea');
    appendResumenHoja('Clase', 'nombreClase');

    const productos = filasProductoExport.map((r) => {
      const pctRent = pctRentabilidad(r.montoTotal, r.costoTotal ?? 0);
      return {
        Producto: r.producto,
        'Tipo de artículo': labelTipoArticulo(r.tipo),
        Ramo: r.nombreRamo ?? '',
        Línea: r.nombreLinea ?? '',
        Clase: r.nombreClase ?? '',
        Cantidad: r.cantidad,
        'Monto Total': r.montoTotal,
        '% Monto': Math.round(pctDelMonto(r.montoTotal, totales.totalMonto) * 100) / 100,
        'Costo Total': r.costoTotal ?? 0,
        '% Rentabilidad': pctRent != null ? Math.round(pctRent * 10) / 10 : '',
        'Peso Total (kg)': r.pesoTotal,
        'Volumen Total (m³)': r.volumenTotal,
      };
    });
    const totalPctRent = pctRentabilidad(totales.totalMonto, totales.totalCosto);
    productos.push({
      Producto: 'TOTAL GENERAL',
      'Tipo de artículo': '',
      Ramo: '',
      Línea: '',
      Clase: '',
      Cantidad: totales.totalCantidad,
      'Monto Total': totales.totalMonto,
      '% Monto': 100,
      'Costo Total': totales.totalCosto,
      '% Rentabilidad': totalPctRent != null ? Math.round(totalPctRent * 10) / 10 : '',
      'Peso Total (kg)': totales.totalPeso,
      'Volumen Total (m³)': totales.totalVolumen,
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(productos), 'Productos');

    XLSX.writeFile(wb, `informe-ventas-producto_${fechaDesde}_${fechaHasta}.xlsx`);
  };

  const handlePrint = () => {
    window.print();
  };

  const abrirDetalle = async (row: VentasResumenProductoRow) => {
    setDetalleTitulo(row.producto || '(sin nombre)');
    setDetalleClave({
      producto: row.producto,
      tipo: row.tipo,
      nombreRamo: row.nombreRamo,
      nombreLinea: row.nombreLinea,
      nombreClase: row.nombreClase,
    });
    setDetalleOpen(true);
    setDetalleLoading(true);
    setDetalleRows([]);
    try {
      const data = await ventasInformesService.postResumenProductoDetalle({
        fechaDesde,
        fechaHasta,
        producto: row.producto,
        tipo: row.tipo ?? '',
        nombreRamo: row.nombreRamo ?? '',
        nombreLinea: row.nombreLinea ?? '',
        nombreClase: row.nombreClase ?? '',
        nroTiposFormulario: nroTiposFormularioParam,
      });
      setDetalleRows(data);
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudo cargar el detalle.', { variant: 'error' });
    } finally {
      setDetalleLoading(false);
    }
  };

  const detalleStats = useMemo(() => {
    const rows = detalleRows;
    const sumSigned = (get: (r: VentasRpt01Row) => number) =>
      rows.reduce((acc, r) => acc + signoVentaResumenNC(r) * get(r), 0);
    const keysForm = new Set(
      rows.map((r) => `${r.nroFormulario ?? ''}|${r.nroTipoFormulario ?? ''}|${r.letra ?? ''}`)
    );
    return {
      lineas: rows.length,
      comprobantes: keysForm.size,
      cantidad: sumSigned((r) => Number(r.iteCantidad ?? 0)),
      monto: sumSigned((r) => Number(r.subtotalFinal ?? r.subtotal ?? 0)),
      costo: sumSigned((r) => Number(r.iteCosto ?? 0) * Number(r.iteCantidad ?? 0)),
      peso: sumSigned((r) => Number(r.pesoTotalKg ?? 0)),
      volumen: sumSigned((r) => Number(r.volumenTotalM3 ?? 0)),
    };
  }, [detalleRows]);

  const detalleUtilidadBruta = useMemo(
    () => detalleStats.monto - detalleStats.costo,
    [detalleStats.monto, detalleStats.costo]
  );

  const detalleRentabilidadPct = useMemo(
    () => pctRentabilidad(detalleStats.monto, detalleStats.costo),
    [detalleStats.monto, detalleStats.costo]
  );

  const exportarDetalleCsv = () => {
    if (detalleRows.length === 0) {
      enqueueSnackbar('No hay líneas para exportar.', { variant: 'info' });
      return;
    }
    const header = [
      'Fecha emisión',
      'Tipo',
      'Comprobante',
      'Fecha vencimiento',
      'Cliente',
      'Cantidad',
      'Precio unitario',
      'Subtotal',
      'Costo unitario',
      'Costo total',
      '% Rentabilidad',
      'Peso (kg)',
      'Volumen (m³)',
    ];
    const lines = [
      header.join(';'),
      ...detalleRows.map((r) => {
        const sign = signoVentaResumenNC(r);
        const cant = Number(r.iteCantidad ?? 0);
        const subtotal = sign * Number(r.subtotalFinal ?? r.subtotal ?? 0);
        const costoTotal = sign * Number(r.iteCosto ?? 0) * cant;
        const pctRent = pctRentabilidad(subtotal, costoTotal);
        return [
          fechaFmtDetalle(r.fechaEmision),
          `"${tipoComprobanteDetalleLabel(r).replace(/"/g, '""')}"`,
          `"${formatoComprobanteDetalle(r).replace(/"/g, '""')}"`,
          fechaFmtDetalle(r.fechaVencim),
          `"${(r.cliNombre ?? r.nombre ?? '').replace(/"/g, '""')}"`,
          String(r.iteCantidad ?? ''),
          String(r.precioUnitarioFinal ?? r.precioUnitario ?? ''),
          String(r.subtotalFinal ?? r.subtotal ?? ''),
          String(r.iteCosto ?? ''),
          String(costoTotal),
          pctRent != null ? String(Math.round(pctRent * 10) / 10) : '',
          String(r.pesoTotalKg ?? ''),
          String(r.volumenTotalM3 ?? ''),
        ].join(';');
      }),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safe = detalleTitulo.replace(/[^\w-]+/g, '_').slice(0, 40);
    a.download = `detalle-ventas_${safe}_${fechaDesde}_${fechaHasta}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportarDetalleExcel = () => {
    if (detalleRows.length === 0) {
      enqueueSnackbar('No hay líneas para exportar.', { variant: 'info' });
      return;
    }
    const data = detalleRows.map((r) => {
      const sign = signoVentaResumenNC(r);
      const cant = Number(r.iteCantidad ?? 0);
      const subtotal = sign * Number(r.subtotalFinal ?? r.subtotal ?? 0);
      const costoTotal = sign * Number(r.iteCosto ?? 0) * cant;
      const pctRent = pctRentabilidad(subtotal, costoTotal);
      return {
        'Fecha emisión': fechaFmtDetalle(r.fechaEmision),
        Tipo: tipoComprobanteDetalleLabel(r),
        Comprobante: formatoComprobanteDetalle(r),
        'Fecha vencimiento': fechaFmtDetalle(r.fechaVencim),
        Cliente: r.cliNombre ?? r.nombre ?? '',
        Cantidad: r.iteCantidad ?? '',
        'Precio unitario': r.precioUnitarioFinal ?? r.precioUnitario ?? '',
        Subtotal: r.subtotalFinal ?? r.subtotal ?? '',
        'Costo unitario': r.iteCosto ?? '',
        'Costo total': costoTotal,
        '% Rentabilidad': pctRent != null ? Math.round(pctRent * 10) / 10 : '',
        'Peso (kg)': r.pesoTotalKg ?? '',
        'Volumen (m³)': r.volumenTotalM3 ?? '',
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Detalle');
    const safe = detalleTitulo.replace(/[^\w-]+/g, '_').slice(0, 40);
    XLSX.writeFile(wb, `detalle-ventas_${safe}_${fechaDesde}_${fechaHasta}.xlsx`);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <style>
        {`
          @media print {
            body * { visibility: hidden; }
            #informe-ventas-print-root, #informe-ventas-print-root * { visibility: visible; }
            #informe-ventas-print-root { position: absolute; left: 0; top: 0; width: 100%; padding: 16px; }
            .no-print { display: none !important; }
            .informe-resumen-clasificacion { page-break-after: avoid; }
          }
        `}
      </style>

      <Card
        id="informe-ventas-print-root"
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              spacing={2}
              sx={{ mb: 3 }}
              className="no-print"
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={2}
                sx={{ flex: 1, minWidth: 0 }}
              >
                <InformesBackButton to="/ventas/informes" useBrowserBack />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    Informe de ventas por producto
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  loading ? <CircularProgress size={22} color="inherit" /> : <DescriptionIcon />
                }
                onClick={() => void handleGenerar()}
                disabled={loading}
                sx={{
                  flexShrink: 0,
                  alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                }}
              >
                Generar Informe
              </Button>
            </Stack>

            <Box className="no-print">
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 2 }}
                alignItems="center"
              >
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600 }}>
                  Período rápido
                </Typography>
                {loadingOpciones && <CircularProgress size={18} />}
                {(
                  [
                    { key: 'hoy' as const, label: 'Hoy' },
                    { key: 'semana' as const, label: 'Semana actual' },
                    { key: 'mes' as const, label: 'Mes actual' },
                    { key: 'mesPasado' as const, label: 'Mes pasado' },
                    { key: 'tresMeses' as const, label: 'Últ. 3 meses' },
                    { key: 'seisMeses' as const, label: 'Últ. 6 meses' },
                    { key: 'anioActual' as const, label: 'Año actual' },
                  ] as const
                ).map(({ key, label }) => (
                  <Chip
                    key={key}
                    icon={
                      <CalendarMonthIcon
                        sx={{
                          fontSize: 18,
                          color: presetActivo === key ? 'inherit' : 'primary.main',
                        }}
                      />
                    }
                    label={label}
                    size="small"
                    onClick={() => applyQuickChip(key)}
                    color={presetActivo === key ? 'primary' : 'default'}
                    variant={presetActivo === key ? 'filled' : 'outlined'}
                  />
                ))}
                <Chip
                  label="Personalizado"
                  size="small"
                  icon={
                    <CalendarMonthIcon
                      sx={{
                        fontSize: 18,
                        color: periodoPersonalizado ? 'inherit' : 'primary.main',
                      }}
                    />
                  }
                  color={periodoPersonalizado ? 'primary' : 'default'}
                  variant={periodoPersonalizado ? 'filled' : 'outlined'}
                  title="Las fechas no coinciden con un período rápido; podés editarlas en Fecha Desde / Hasta."
                  sx={{ cursor: 'default' }}
                />
              </Stack>

              <Accordion
                variant="outlined"
                disableGutters
                sx={{
                  borderRadius: 2,
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ bgcolor: '#fafafa', minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, pr: 1 }}>
                    <FilterAltIcon color="primary" fontSize="small" />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Filtros avanzados
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {resumenFiltrosAvanzados}
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 2 }}>
              <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField                  
                  label="Código de artículo"
                  value={codigoArticuloFiltro}
                  onChange={(e) => {
                    setCodigoArticuloFiltro(e.target.value);
                    setDescripcionArticuloFiltro('');
                  }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          color="primary"
                          title="Buscar producto"
                          onClick={abrirBuscadorProductos}
                        >
                          <ArrowDropDownIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Descripción artículo"
                  value={descripcionArticuloFiltro}                  
                  onChange={(e) => {
                    setDescripcionArticuloFiltro(e.target.value);
                    setProductoSearchTerm(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setDialogProductoOpen(true);
                      void buscarProductos((e.target as HTMLInputElement).value);
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          color="primary"
                          title="Buscar producto por descripción"
                          onClick={abrirBuscadorProductos}
                        >
                          <ArrowDropDownIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Tipo de Artículo"
                  size="small"
                  allowEmpty
                  emptyOptionLabel="Todos los tipos"
                  value={tipoArticulo || null}
                  onChange={(v) => setTipoArticulo(v ?? '')}
                  options={TIPO_ARTICULO_OPTIONS.map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Ramo"
                  size="small"
                  allowEmpty
                  emptyOptionLabel="Todos los ramos"
                  value={nombreRamo || null}
                  onChange={(v) => setNombreRamo(v ?? '')}
                  options={opcionesRamos.map((x) => ({ value: x, label: x }))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Línea"
                  size="small"
                  allowEmpty
                  emptyOptionLabel="Todas las líneas"
                  value={nombreLinea || null}
                  onChange={(v) => setNombreLinea(v ?? '')}
                  options={opcionesLineas.map((x) => ({ value: x, label: x }))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Clase"
                  size="small"
                  allowEmpty
                  emptyOptionLabel="Todas las clases"
                  value={nombreClase || null}
                  onChange={(v) => setNombreClase(v ?? '')}
                  options={opcionesClases.map((x) => ({ value: x, label: x }))}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Tipo de comprobante
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} alignItems="center">
                    {TIPOS_COMPROBANTE_INFORME_VENTAS.map((opt) => (
                      <Chip
                        key={opt.value}
                        label={opt.label}
                        size="small"
                        onClick={() => toggleTipoComprobante(opt.value)}
                        color={tiposComprobanteSel.includes(opt.value) ? 'primary' : 'default'}
                        variant={tiposComprobanteSel.includes(opt.value) ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
                </AccordionDetails>
              </Accordion>
            </Box>

            <Divider className="no-print" sx={{ my: 3 }} />

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <TableChartIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Resultados del Informe
                </Typography>
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={1} className="no-print">
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<DescriptionIcon />}
                  onClick={exportarCsv}
                  disabled={rows.length === 0}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                >
                  Exportar CSV
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<GridOnIcon />}
                  onClick={exportarExcel}
                  disabled={rows.length === 0}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                >
                  Exportar Excel
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  disabled={rows.length === 0}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                >
                  Imprimir
                </Button>
              </Stack>
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Totales por ramo, línea y clase; detalle por producto en la solapa Producto.
            </Typography>

            {rows.length > 0 ? (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<AttachMoneyIcon fontSize="small" />}
                    titulo="Monto neto total"
                    valor={currencyFormatter.format(indicadoresResumen.m)}
                    tooltip="Suma de subtotales finales de línea (notas de crédito restan)."
                    colorIcono="#2e7d32"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<FormatListNumberedIcon fontSize="small" />}
                    titulo="Cantidad total"
                    valor={cantidadSumFormatter.format(indicadoresResumen.cq)}
                    tooltip="Unidades netas (notas de crédito restan cantidad)."
                    colorIcono="#1565c0"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<ReceiptLongIcon fontSize="small" />}
                    titulo="Total de comprobantes"
                    valor={indicadoresResumen.nComp.toLocaleString('es-AR')}
                    detalle=""
                    tooltip="Cantidad de comprobantes distintos en el período y filtros (por Nro. interno o sucursal / tipo / nro.)."
                    colorIcono="#6a1b9a"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<PaymentsIcon fontSize="small" />}
                    titulo="Ticket promedio"
                    valor={
                      indicadoresResumen.ticketPromedio != null
                        ? currencyFormatter.format(indicadoresResumen.ticketPromedio)
                        : '—'
                    }
                    detalle="Monto neto ÷ comprobantes"
                    tooltip="Monto neto del informe dividido por la cantidad de comprobantes distintos."
                    colorIcono="#c62828"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<PriceChangeIcon fontSize="small" />}
                    titulo="Precio medio / unidad"
                    valor={
                      indicadoresResumen.precioMedioUnitario != null
                        ? currencyFormatter.format(indicadoresResumen.precioMedioUnitario)
                        : '—'
                    }
                    detalle="Monto neto ÷ cantidad"
                    tooltip="Cociente entre monto neto total y cantidad total (promedio ponderado)."
                    colorIcono="#ef6c00"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<ShoppingCartIcon fontSize="small" />}
                    titulo="Costo total"
                    valor={currencyFormatter.format(indicadoresResumen.c)}
                    tooltip="Suma de costo unitario congelado × cantidad (notas de crédito restan)."
                    colorIcono="#795548"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<PaidIcon fontSize="small" />}
                    titulo="Utilidad bruta"
                    valor={currencyFormatter.format(indicadoresResumen.utilidadBruta)}
                    detalle="Monto neto − costo"
                    tooltip="Diferencia entre monto neto de venta y costo total (notas de crédito restan en ambos)."
                    colorIcono="#2e7d32"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<TrendingUpIcon fontSize="small" />}
                    titulo="Rentabilidad"
                    valor={
                      indicadoresResumen.rentabilidadPct != null
                        ? `${pctFormatter.format(indicadoresResumen.rentabilidadPct)}%`
                        : '—'
                    }
                    detalle="(Monto − Costo) ÷ Monto"
                    tooltip="Porcentaje de utilidad bruta sobre el monto neto de venta."
                    colorIcono="#00695c"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<GridViewIcon fontSize="small" />}
                    titulo="Clasificación comercial"
                    valor={`${conteosClasificacion.ramos} · ${conteosClasificacion.lineas} · ${conteosClasificacion.clases}`}
                    detalle={`${rows.length.toLocaleString('es-AR')} productos`}
                    tooltip="Ramos, líneas y clases distintos; filas de detalle en solapa Producto."
                    colorIcono="#00838f"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<ScaleIcon fontSize="small" />}
                    titulo="Peso total"
                    valor={`${decimal3Formatter.format(indicadoresResumen.p)} kg`}
                    tooltip="Peso neto total de las líneas (notas de crédito restan)."
                    colorIcono="#5d4037"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<ViewInArIcon fontSize="small" />}
                    titulo="Volumen total"
                    valor={`${decimal3Formatter.format(indicadoresResumen.v)} m³`}
                    tooltip="Volumen neto total (notas de crédito restan)."
                    colorIcono="#455a64"
                  />
                </Grid>
              </Grid>
            ) : null}

            {loading && rows.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4 }}>
                Configurá filtros y pulsá «Generar Informe».
              </Typography>
            ) : (
              <ResumenClasificacionTabs
                rows={rows}
                totalMonto={totales.totalMonto}
                totales={totales}
                onAbrirDetalle={(row) => void abrirDetalle(row)}
              />
            )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogProductoOpen}
        onClose={() => setDialogProductoOpen(false)}
        maxWidth="md"
        fullWidth
        className="no-print"
      >
        <DialogTitle>Buscar producto</DialogTitle>
        <DialogContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, mt: 0.5 }}>
            <TextField
              label="Código o descripción"
              value={productoSearchTerm}
              onChange={(e) => setProductoSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void buscarProductos();
                }
              }}
              size="small"
              fullWidth
            />
            <Button
              variant="contained"
              onClick={() => void buscarProductos()}
              disabled={productoSearchLoading}
            >
              Buscar
            </Button>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="center">Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productoSearchLoading && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                )}
                {!productoSearchLoading &&
                  productosEncontrados.map((producto) => (
                    <TableRow key={producto.codigoArticulo} hover>
                      <TableCell>{producto.codigoArticulo}</TableCell>
                      <TableCell>{producto.descripcion ?? '—'}</TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => seleccionarProducto(producto)}
                        >
                          Seleccionar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                {!productoSearchLoading && productosEncontrados.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        Sin resultados.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogProductoOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detalleOpen}
        onClose={() => setDetalleOpen(false)}
        maxWidth="xl"
        fullWidth
        className="no-print"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            maxHeight: 'calc(100% - 48px)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            pr: 1,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" component="span" fontWeight={700}>
            Detalle — {detalleTitulo}
            {detalleClave?.tipo ? ` · ${labelTipoArticulo(detalleClave.tipo)}` : ''}
            {detalleClave?.nombreRamo ? ` · ${detalleClave.nombreRamo}` : ''}
            {detalleClave?.nombreLinea ? ` · ${detalleClave.nombreLinea}` : ''}
            {detalleClave?.nombreClase ? ` · ${detalleClave.nombreClase}` : ''}
          </Typography>
          <IconButton
            aria-label="cerrar"
            size="small"
            onClick={() => setDetalleOpen(false)}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          {detalleLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Total líneas
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#1565c0' }}>
                    {qtyFormatter.format(detalleStats.lineas)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Comprobantes
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#2e7d32' }}>
                    {qtyFormatter.format(detalleStats.comprobantes)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Cantidad acumulada
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#7b1fa2' }}>
                    {cantidadSumFormatter.format(detalleStats.cantidad)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Monto total
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#c62828' }}>
                    {currencyFormatter.format(detalleStats.monto)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Costo total
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#795548' }}>
                    {currencyFormatter.format(detalleStats.costo)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Utilidad bruta
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#2e7d32' }}>
                    {currencyFormatter.format(detalleUtilidadBruta)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Rentabilidad
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#00695c' }}>
                    {detalleRentabilidadPct != null ? `${pctFormatter.format(detalleRentabilidadPct)}%` : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Peso total
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#616161' }}>
                    {decimal3Formatter.format(detalleStats.peso)} kg
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Volumen total
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#616161' }}>
                    {decimal3Formatter.format(detalleStats.volumen)} m³
                  </Typography>
                </Grid>
              </Grid>

              {detalleRows.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 3 }}>
                  No hay líneas en el detalle.
                </Typography>
              ) : (
                <TableContainer
                  sx={{
                    maxHeight: 440,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ '& th': { bgcolor: '#f5f5f5', fontWeight: 700 } }}>
                        <TableCell>Fecha emisión</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Comprobante</TableCell>
                        <TableCell>Fecha vencimiento</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell align="right">Cantidad</TableCell>
                        <TableCell align="right">Precio unitario</TableCell>
                        <TableCell align="right">Subtotal</TableCell>
                        <TableCell align="right">Costo unit.</TableCell>
                        <TableCell align="right">Costo total</TableCell>
                        <TableCell align="right">% Rentab.</TableCell>
                        <TableCell align="right">Peso (kg)</TableCell>
                        <TableCell align="right">Volumen (m³)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detalleRows.map((r, i) => (
                        (() => {
                          const esNC = isNotaCreditoDetalle(r);
                          const colorNc = esNC ? 'error.main' : 'text.primary';
                          const cantidadSigned =
                            r.iteCantidad != null ? signoVentaResumenNC(r) * Math.abs(Number(r.iteCantidad)) : null;
                          const precioSigned =
                            r.precioUnitarioFinal != null || r.precioUnitario != null
                              ? signoVentaResumenNC(r) *
                                Math.abs(Number(r.precioUnitarioFinal ?? r.precioUnitario ?? 0))
                              : null;
                          const subtotalBase = r.subtotalFinal ?? r.subtotal;
                          const subtotalSigned =
                            subtotalBase != null ? signoVentaResumenNC(r) * Math.abs(Number(subtotalBase)) : null;
                          const costoUnitSigned =
                            r.iteCosto != null ? Math.abs(Number(r.iteCosto)) : null;
                          const costoTotalSigned =
                            r.iteCosto != null && r.iteCantidad != null
                              ? signoVentaResumenNC(r) * Math.abs(Number(r.iteCosto) * Number(r.iteCantidad))
                              : null;
                          const pctRentLinea =
                            subtotalSigned != null && costoTotalSigned != null
                              ? pctRentabilidad(subtotalSigned, costoTotalSigned)
                              : null;
                          return (
                        <TableRow
                          key={`${r.nroFormulario}-${r.nroItem}-${i}`}
                          sx={{
                            '&:nth-of-type(even)': { bgcolor: '#fafafa' },
                          }}
                        >
                          <TableCell>{fechaFmtDetalle(r.fechaEmision)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{tipoComprobanteDetalleLabel(r)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatoComprobanteDetalle(r)}</TableCell>
                          <TableCell>{fechaFmtDetalle(r.fechaVencim)}</TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>{r.cliNombre ?? r.nombre ?? '—'}</TableCell>
                          <TableCell align="right" sx={{ color: colorNc, fontWeight: esNC ? 700 : 400 }}>
                            {cantidadSigned != null ? cantidadSumFormatter.format(cantidadSigned) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: colorNc, fontWeight: esNC ? 700 : 400 }}>
                            {precioSigned != null ? currencyFormatter.format(precioSigned) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: colorNc, fontWeight: esNC ? 700 : 400 }}>
                            {subtotalSigned != null ? currencyFormatter.format(subtotalSigned) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: colorNc, fontWeight: esNC ? 700 : 400 }}>
                            {costoUnitSigned != null ? currencyFormatter.format(costoUnitSigned) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: colorNc, fontWeight: esNC ? 700 : 400 }}>
                            {costoTotalSigned != null ? currencyFormatter.format(costoTotalSigned) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: colorNc, fontWeight: esNC ? 700 : 400 }}>
                            {pctRentLinea != null ? `${pctFormatter.format(pctRentLinea)}%` : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {r.pesoTotalKg != null ? decimal3Formatter.format(r.pesoTotalKg) : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {r.volumenTotalM3 != null ? decimal3Formatter.format(r.volumenTotalM3) : '—'}
                          </TableCell>
                        </TableRow>
                          );
                        })()
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DescriptionIcon />}
              onClick={exportarDetalleCsv}
              disabled={detalleRows.length === 0}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
            >
              Exportar CSV
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<GridOnIcon />}
              onClick={exportarDetalleExcel}
              disabled={detalleRows.length === 0}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
            >
              Exportar Excel
            </Button>
          </Stack>
          <Button
            variant="text"
            color="primary"
            onClick={() => setDetalleOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InformeVentasResumenProducto;
